import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import {
  deleteOrgLogo,
  getAuthenticatedLogoUrl,
  getOrgSettings,
  notifyOrgBrandingChanged,
  type OrgSettings,
  updateOrgSettings,
  uploadOrgLogo,
} from '../../api/org'

const inputClassName =
  'w-full px-4 py-3 bg-harbor-surface border border-harbor-border rounded-lg text-harbor-text placeholder-harbor-text-muted focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent'

/**
 * Logo preview that never renders a broken-image icon: a stale logoUrl hides
 * the <img> via onError. `key`d by logoUrl so upload/removal resets state.
 */
function OrgLogoPreview({ name, logoSrc }: { name: string; logoSrc: string | null }) {
  const [logoBroken, setLogoBroken] = useState(false)
  if (logoSrc && !logoBroken) {
    return (
      <img
        src={logoSrc}
        alt={`${name} logo`}
        width={64}
        height={64}
        onError={() => setLogoBroken(true)}
        className="rounded-xl object-cover shrink-0 overflow-hidden border border-harbor-border"
      />
    )
  }
  return (
    <div className="w-16 h-16 rounded-xl border border-dashed border-harbor-border flex items-center justify-center text-2xl text-harbor-text-muted">
      {name.charAt(0).toUpperCase()}
    </div>
  )
}

export default function OrgSettingsPage() {
  const [org, setOrg] = useState<OrgSettings | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [name, setName] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [isUploadingLogo, setIsUploadingLogo] = useState(false)
  const [isRemovingLogo, setIsRemovingLogo] = useState(false)
  const logoInputRef = useRef<HTMLInputElement>(null)

  const loadOrg = useCallback(async () => {
    try {
      const data = await getOrgSettings()
      setOrg(data)
      setName(data.name)
    } catch {
      toast.error('Failed to load organization settings')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadOrg()
  }, [loadOrg])

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = name.trim()
    if (trimmed.length === 0) {
      toast.error('Organization name is required')
      return
    }
    setIsSaving(true)
    try {
      const updated = await updateOrgSettings(trimmed)
      setOrg(updated)
      setName(updated.name)
      notifyOrgBrandingChanged()
      toast.success('Organization settings saved')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save organization')
    } finally {
      setIsSaving(false)
    }
  }

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setIsUploadingLogo(true)
    try {
      const { logoUrl } = await uploadOrgLogo(file)
      setOrg((prev) => (prev ? { ...prev, logoUrl } : prev))
      notifyOrgBrandingChanged()
      toast.success('Logo uploaded')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to upload logo')
    } finally {
      setIsUploadingLogo(false)
      if (logoInputRef.current) logoInputRef.current.value = ''
    }
  }

  const handleRemoveLogo = async () => {
    setIsRemovingLogo(true)
    try {
      await deleteOrgLogo()
      setOrg((prev) => (prev ? { ...prev, logoUrl: null } : prev))
      notifyOrgBrandingChanged()
      toast.success('Logo removed')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to remove logo')
    } finally {
      setIsRemovingLogo(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-emerald-500" />
      </div>
    )
  }

  if (!org) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="rounded-xl border border-harbor-border bg-harbor-surface/40 p-6 text-sm text-harbor-text-secondary">
          Organization settings could not be loaded.
        </div>
      </div>
    )
  }

  const logoSrc = getAuthenticatedLogoUrl(org.logoUrl)

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-harbor-text">Organization</h1>
        <p className="text-harbor-text-secondary mt-1">
          Manage your organization profile and branding.
        </p>
      </div>

      <form
        onSubmit={handleSave}
        className="rounded-xl border border-harbor-border bg-harbor-surface/40 p-6 space-y-5"
      >
        <h2 className="text-lg font-semibold text-harbor-text">General</h2>
        <div>
          <label
            htmlFor="org-settings-slug"
            className="block text-sm font-medium text-harbor-text-secondary mb-1"
          >
            Slug
          </label>
          <input
            id="org-settings-slug"
            type="text"
            value={org.slug}
            disabled
            readOnly
            title="The organization slug cannot be changed"
            className={`${inputClassName} opacity-60 cursor-not-allowed font-mono`}
          />
          <p className="mt-1 text-xs text-harbor-text-muted">
            The slug is part of your URLs and cannot be changed.
          </p>
        </div>
        <div>
          <label
            htmlFor="org-settings-name"
            className="block text-sm font-medium text-harbor-text-secondary mb-1"
          >
            Name
          </label>
          <input
            id="org-settings-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="Acme Corp"
            className={inputClassName}
          />
        </div>
        {org.workspaceDomain && (
          <div>
            <span className="block text-sm font-medium text-harbor-text-secondary mb-1">
              Email domain
            </span>
            <p className="text-sm text-harbor-text">{org.workspaceDomain}</p>
          </div>
        )}
        <button
          type="submit"
          disabled={isSaving}
          className="px-5 py-2.5 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSaving ? 'Saving...' : 'Save changes'}
        </button>
      </form>

      <div className="rounded-xl border border-harbor-border bg-harbor-surface/40 p-6 space-y-5">
        <div>
          <h2 className="text-lg font-semibold text-harbor-text">Logo</h2>
          <p className="text-sm text-harbor-text-secondary mt-1">
            Shown next to ContextHarbor branding in the sidebar header. PNG, JPG, WebP, or SVG up to
            5 MB.
          </p>
        </div>

        <div className="flex items-center gap-4">
          <OrgLogoPreview key={org.logoUrl ?? 'no-logo'} name={org.name} logoSrc={logoSrc} />
          <div className="flex flex-wrap gap-2">
            <input
              ref={logoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              onChange={(e) => void handleLogoChange(e)}
              className="hidden"
            />
            <button
              type="button"
              disabled={isUploadingLogo}
              onClick={() => logoInputRef.current?.click()}
              className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isUploadingLogo ? 'Uploading...' : org.logoUrl ? 'Change logo' : 'Upload logo'}
            </button>
            {org.logoUrl && (
              <button
                type="button"
                disabled={isRemovingLogo}
                onClick={() => void handleRemoveLogo()}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-red-700/50 text-red-300 hover:bg-red-900/30 disabled:opacity-50 transition-colors"
              >
                {isRemovingLogo ? 'Removing...' : 'Remove logo'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
